import { Signal } from "signals";
import { Services } from "slotworks/services/services";
import { SoundEvent } from "slotworks/services/sound/sound-events";
import Contract from "slotworks/utils/contracts/contract";
import Timer from "slotworks/utils/timer";
import * as TweenJS from "slotworks/utils/tween";
import { isNullOrUndefined } from "util";

export class Transaction {

    public currentWinUpdate: Signal = new Signal();
    public totalWinUpdate: Signal = new Signal();
    public balanceUpdate: Signal = new Signal();

    protected previousBalance: number = -1;

    private pendingUpdate: number;
    private currentWin: CurrentWinTicker;
    private totalWin: WinTicker;

    constructor() {
        this.currentWin = new CurrentWinTicker();
        this.totalWin = new WinTicker();

        this.currentWin.onUpdate.add((value: number) => this.currentWinUpdate.dispatch(value));
        this.totalWin.onUpdate.add((value: number) => this.totalWinUpdate.dispatch(value));
    }

    public setBalance(value: number) {
        this.balanceUpdate.dispatch(value, this.previousBalance);
        this.previousBalance = value;
    }

    public setDelayedWinnings(currentWin: number, totalWin?: number, delay: number = 0, tickTime: number = 0): Contract<void> {
        return new Contract<void>((resolve) => {
            Timer.clearTimeout(this.pendingUpdate);

            this.pendingUpdate = Timer.setTimeout(() => {
                this.setWinnings(currentWin, totalWin, tickTime).then(() => resolve(null));
            }, delay);
        });
    }

    public setWinnings(currentWin: number, totalWin?: number, tickTime: number = 0): Contract<void> {
        return new Contract<void>((resolve) => {
            Timer.clearTimeout(this.pendingUpdate);

            if (isNullOrUndefined(currentWin)) {
                currentWin = 0;
            }

            if (totalWin === undefined) {
                totalWin = currentWin;
            }

            this.setWin(this.totalWin, totalWin, tickTime).execute();
            this.setWin(this.currentWin, currentWin, tickTime).then(() => resolve(null));
        });
    }

    public setTotalWin(totalWin: number) {
        this.setWinnings(0, totalWin).execute();
    }

    private setWin(win: WinTicker, value: number, tickTime: number) {
        return new Contract<void>((resolve) => {
            if (!win.actionNeeded(value, tickTime)) {
                resolve(null);
                return;
            }

            if (win.ticking()) {
                win.stop();
            }

            win.start(value, tickTime).then(() => resolve(null));
        });
    }
}

class WinTicker {
    public onUpdate: Signal = new Signal();
    public tickingValue: number = 0;
    public totalValue: number = 0;
    protected tween: TweenJS.Tween = null;

    public actionNeeded(value: number, tickTime: number) {
        const newIsValid = value !== null && !isNaN(value);
        const newIsSameAsCurrent = value !== this.totalValue;
        const skippingCurrentTickingValue = (tickTime === 0 && this.ticking());
        return newIsValid && (newIsSameAsCurrent || skippingCurrentTickingValue);
    }

    public ticking(): boolean {
        return this.tween !== null;
    }

    public stop() {
        if (this.ticking()) {
            this.tween.stop();
        }
        this.tween = null;
    }

    public start(value: number, tickTime: number): Contract<void> {
        if (tickTime > 0 && value > 0) {
            return this.tickUp(value, tickTime);
        } else {
            return this.go(value);
        }
    }

    public update(value: number) {
        this.onUpdate.dispatch(value);
    }

    protected tickUp(value: number, tickTime: number) {

        return new Contract<void>((resolve) => {
            // protect against ticking backwards
            if (value < this.totalValue) {
                this.totalValue = 0;
            }

            this.tickingValue = this.totalValue;
            const tickObject = { t: this.tickingValue };
            this.totalValue = value;

            this.tween = new TweenJS.Tween(tickObject)
                .to({ t: value }, tickTime)
                .onUpdate(() => {
                    this.tickingValue = tickObject.t;
                    this.update(this.tickingValue);
                })
                .onComplete(() => {
                    this.update(this.totalValue);
                    this.tickingValue = this.totalValue;
                    this.stop();
                    resolve(null);
                })
                .start();
        });
    }

    protected go(value: number) {
        this.tickingValue = value;
        this.totalValue = value;
        this.update(value);
        return Contract.empty();
    }
}

class CurrentWinTicker extends WinTicker {
    public onUpdate: Signal = new Signal();

    public start(value: number, tickTime: number): Contract<void> {
        if (value > 0 && tickTime > 0) {
            Services.sound.event(SoundEvent.winnings_countup);
        }

        return super.start(value, tickTime);
    }

    public stop() {
        super.stop();
        Services.sound.event(SoundEvent.winnings_countup_end);
    }

    public update(value: number) {
        this.onUpdate.dispatch(value);
    }

    protected tickUp(value: number, tickTime: number) {
        // always reset to 0 and count up from there for current win
        this.totalValue = 0;

        return super.tickUp(value, tickTime);
    }
}
